import { useState } from "react";
import { api } from "../../api/client";
import type { StaffSnapshot } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { dateTime, money } from "../../ui/format";
import { ScheduleFields, SlugPicker } from "./PromoCodes";

type Promotion = StaffSnapshot["promotions"][number];
type PromotionType = Promotion["type"];

/** Типы акций из п. 19 ТЗ и подсказки, что означает поле «значение». */
const TYPES: { id: PromotionType; title: string; hint: string; valueLabel: string | null }[] = [
  {
    id: "product_discount",
    title: "Скидка на товар или категорию",
    hint: "Процент от стоимости подходящих позиций.",
    valueLabel: "Процент скидки"
  },
  {
    id: "special_price",
    title: "Специальная цена",
    hint: "Новая цена за штуку для выбранных товаров.",
    valueLabel: "Цена за штуку, ₽"
  },
  {
    id: "two_for_one",
    title: "Два по цене одного",
    hint: "Каждая вторая единица позиции бесплатна.",
    valueLabel: null
  },
  {
    id: "bundle_fixed_price",
    title: "Набор за фиксированную цену",
    hint: "Работает, когда в корзине есть все товары набора.",
    valueLabel: null
  },
  {
    id: "gift_over_amount",
    title: "Подарок от суммы",
    hint: "Товар добавляется в заказ бесплатно.",
    valueLabel: null
  },
  {
    id: "free_modifier",
    title: "Бесплатная добавка",
    hint: "Обнуляет стоимость выбранной добавки в подходящих позициях.",
    valueLabel: null
  }
];

const WEEKDAY_TITLES = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

function describe(promotion: Promotion): string {
  switch (promotion.type) {
    case "product_discount":
      return `−${promotion.value}%`;
    case "special_price":
      return `по ${money(promotion.value)}`;
    case "two_for_one":
      return "2 по цене 1";
    case "bundle_fixed_price":
      return `набор за ${money(promotion.bundlePrice ?? 0)}`;
    case "gift_over_amount":
      return `подарок от ${money(promotion.minAmount)}`;
    case "free_modifier":
      return "бесплатная добавка";
    default:
      return "";
  }
}

export function PromotionsPage() {
  const { snapshot } = useAppState();
  const { run } = useToasts();
  const [editing, setEditing] = useState<Promotion | null | undefined>(undefined);

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  async function toggleActive(promotion: Promotion, active: boolean) {
    await run(() => api.patch(`/api/admin/promotions/${promotion.id}`, { active }));
  }

  async function remove(promotion: Promotion) {
    if (!confirm(`Удалить акцию «${promotion.title}»?`)) return;
    await run(() => api.delete(`/api/admin/promotions/${promotion.id}`), "Акция удалена");
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Маркетинг</p>
          <h1>Акции</h1>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setEditing(null)}>
          + Акция
        </button>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Действующие акции</h2>
            <p>Акции применяются к корзине автоматически, в порядке приоритета.</p>
          </div>
        </header>

        {snapshot.promotions.length === 0 ? (
          <Empty>Акций пока нет.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Акция</th>
                  <th>Условие</th>
                  <th>Товары</th>
                  <th>Расписание</th>
                  <th>Приоритет</th>
                  <th>Активна</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {snapshot.promotions.map(promotion => (
                  <tr key={promotion.id}>
                    <td>
                      <strong>{promotion.title}</strong>
                      <br />
                      <small className="muted">
                        {TYPES.find(type => type.id === promotion.type)?.title ?? promotion.type}
                      </small>
                    </td>
                    <td className="num">{describe(promotion)}</td>
                    <td className="tiny">
                      {promotion.productSlugs.length || promotion.categorySlugs.length
                        ? [...promotion.productSlugs, ...promotion.categorySlugs].join(", ")
                        : "вся корзина"}
                    </td>
                    <td className="tiny">
                      {promotion.startsAt || promotion.endsAt
                        ? `${dateTime(promotion.startsAt)} — ${dateTime(promotion.endsAt)}`
                        : "без срока"}
                      <br />
                      <span className="muted">
                        {promotion.weekdays.length
                          ? promotion.weekdays.map(day => WEEKDAY_TITLES[day]).join(", ")
                          : "любой день"}
                        {promotion.hourFrom !== null && promotion.hourTo !== null
                          ? `, ${promotion.hourFrom}:00–${promotion.hourTo}:00`
                          : ""}
                      </span>
                      <br />
                      <span className="muted">
                        {promotion.stacksWithPromo ? "совместима с промокодом" : "не совместима с промокодом"}
                      </span>
                    </td>
                    <td className="num">{promotion.priority}</td>
                    <td>
                      <Toggle
                        checked={promotion.active}
                        label={`Акция «${promotion.title}» активна`}
                        onChange={next => void toggleActive(promotion, next)}
                      />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-sm" onClick={() => setEditing(promotion)}>
                          Изменить
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => void remove(promotion)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing !== undefined ? (
        <PromotionForm promotion={editing} snapshot={snapshot} onClose={() => setEditing(undefined)} />
      ) : null}
    </>
  );
}

function PromotionForm({
  promotion,
  snapshot,
  onClose
}: {
  promotion: Promotion | null;
  snapshot: StaffSnapshot;
  onClose: () => void;
}) {
  const { run } = useToasts();
  const [form, setForm] = useState({
    title: promotion?.title ?? "",
    description: promotion?.description ?? "",
    type: promotion?.type ?? ("product_discount" as PromotionType),
    value: String(promotion?.value ?? 10),
    productSlugs: promotion?.productSlugs ?? [],
    categorySlugs: promotion?.categorySlugs ?? [],
    bundlePrice: promotion?.bundlePrice ? String(promotion.bundlePrice) : "",
    minAmount: String(promotion?.minAmount ?? 0),
    giftProductSlug: promotion?.giftProductSlug ?? "",
    freeModifierSlug: promotion?.freeModifierSlug ?? "",
    weekdays: promotion?.weekdays ?? [],
    hourFrom: promotion?.hourFrom !== null && promotion?.hourFrom !== undefined ? String(promotion.hourFrom) : "",
    hourTo: promotion?.hourTo !== null && promotion?.hourTo !== undefined ? String(promotion.hourTo) : "",
    priority: String(promotion?.priority ?? 0),
    stacksWithPromo: promotion?.stacksWithPromo ?? true
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  const meta = TYPES.find(type => type.id === form.type)!;
  const optionalNumber = (value: string) => (value.trim() ? Number(value) : null);

  // Все добавки меню — для акции «бесплатная добавка».
  const modifierOptions = snapshot.products.flatMap(product =>
    product.modifiers.flatMap(group =>
      group.options
        .filter(option => !option.exclusion)
        .map(option => ({ id: option.id, title: `${option.title} (${product.title})` }))
    )
  );

  async function save() {
    const body = {
      title: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      value: Number(form.value) || 0,
      productSlugs: form.productSlugs,
      categorySlugs: form.categorySlugs,
      bundlePrice: optionalNumber(form.bundlePrice),
      minAmount: Number(form.minAmount) || 0,
      giftProductSlug: form.giftProductSlug || null,
      freeModifierSlug: form.freeModifierSlug || null,
      weekdays: form.weekdays,
      hourFrom: optionalNumber(form.hourFrom),
      hourTo: optionalNumber(form.hourTo),
      priority: Number(form.priority) || 0,
      stacksWithPromo: form.stacksWithPromo
    };

    const ok = await run(
      () =>
        promotion
          ? api.patch(`/api/admin/promotions/${promotion.id}`, body)
          : api.post("/api/admin/promotions", body),
      promotion ? "Акция сохранена" : "Акция создана"
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={promotion ? `Акция «${promotion.title}»` : "Новая акция"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!form.title.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название для клиента</span>
        <input
          autoFocus
          value={form.title}
          onChange={event => update("title", event.target.value)}
          placeholder="Два бургера по цене одного"
        />
      </label>

      <label className="field">
        <span>Тип акции</span>
        <select value={form.type} onChange={event => update("type", event.target.value as PromotionType)}>
          {TYPES.map(type => (
            <option key={type.id} value={type.id}>
              {type.title}
            </option>
          ))}
        </select>
      </label>
      <p className="tiny muted">{meta.hint}</p>

      {meta.valueLabel ? (
        <label className="field">
          <span>{meta.valueLabel}</span>
          <input inputMode="numeric" value={form.value} onChange={event => update("value", event.target.value)} />
        </label>
      ) : null}

      {form.type === "bundle_fixed_price" ? (
        <label className="field">
          <span>Цена набора, ₽</span>
          <input
            inputMode="numeric"
            value={form.bundlePrice}
            onChange={event => update("bundlePrice", event.target.value)}
          />
        </label>
      ) : null}

      {form.type === "gift_over_amount" ? (
        <>
          <label className="field">
            <span>Сумма заказа от, ₽</span>
            <input
              inputMode="numeric"
              value={form.minAmount}
              onChange={event => update("minAmount", event.target.value)}
            />
          </label>
          <label className="field">
            <span>Товар в подарок</span>
            <select value={form.giftProductSlug} onChange={event => update("giftProductSlug", event.target.value)}>
              <option value="">— выберите —</option>
              {snapshot.products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.title}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}

      {form.type === "free_modifier" ? (
        <label className="field">
          <span>Бесплатная добавка</span>
          <select value={form.freeModifierSlug} onChange={event => update("freeModifierSlug", event.target.value)}>
            <option value="">— выберите —</option>
            {modifierOptions.map(option => (
              <option key={`${option.id}-${option.title}`} value={option.id}>
                {option.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {form.type !== "gift_over_amount" ? (
        <>
          <SlugPicker
            label={form.type === "bundle_fixed_price" ? "Товары набора" : "Товары"}
            options={snapshot.products.map(product => ({ id: product.id, title: product.title }))}
            selected={form.productSlugs}
            onChange={next => update("productSlugs", next)}
          />
          {form.type !== "bundle_fixed_price" ? (
            <SlugPicker
              label="Категории"
              options={snapshot.categories.map(category => ({ id: category.id, title: category.title }))}
              selected={form.categorySlugs}
              onChange={next => update("categorySlugs", next)}
            />
          ) : null}
          <p className="tiny muted">Если ничего не выбрано, акция действует на всю корзину.</p>
        </>
      ) : null}

      <ScheduleFields
        weekdays={form.weekdays}
        hourFrom={form.hourFrom}
        hourTo={form.hourTo}
        onWeekdays={next => update("weekdays", next)}
        onHourFrom={next => update("hourFrom", next)}
        onHourTo={next => update("hourTo", next)}
      />

      <div className="field-row">
        <label className="field">
          <span>Приоритет</span>
          <input inputMode="numeric" value={form.priority} onChange={event => update("priority", event.target.value)} />
        </label>
      </div>
      <p className="tiny muted">Меньшее значение применяется раньше — это важно, когда акций несколько.</p>

      <label className="inline-check">
        <input
          type="checkbox"
          checked={form.stacksWithPromo}
          onChange={event => update("stacksWithPromo", event.target.checked)}
        />
        Можно применять вместе с промокодом
      </label>
    </Modal>
  );
}
